// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type DebtBreakdownSlice } from '@shared/advisory/aggregation/utils/build-debt-breakdown';

export type { DebtBreakdownSlice };

export type DebtBreakdownView = 'bank' | 'type';

export const useDebtBreakdownChart = ({
  breakdownByBank,
  breakdownByType,
}: {
  breakdownByBank: DebtBreakdownSlice;
  breakdownByType: DebtBreakdownSlice;
}) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<DebtBreakdownView>('bank');

  const activeBreakdown = useMemo(
    () => (viewMode === 'bank' ? breakdownByBank : breakdownByType),
    [breakdownByBank, breakdownByType, viewMode],
  );

  const title =
    viewMode === 'bank'
      ? t('aggregator.loans.breakdown-by-bank', { ns: 'aggregator' })
      : t('aggregator.loans.breakdown-by-type', { ns: 'aggregator' });

  return {
    t,
    viewMode,
    setViewMode,
    title,
    exposure: activeBreakdown.labels,
    weights: activeBreakdown.weights,
  };
};
